import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { parseCoverDoc } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { CoverThumb } from '../canvas/CoverThumb';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { designsByAge } from './designsByAge';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface VersionsStripProps {
  onOpen: (designUuid: string) => void;
  onNew: () => void;
  onDelete: (designUuid: string) => void;
  isBusy: boolean;
}

const THUMB_PX = 54;

/**
 * The versions of the design (design cover-editor b1): the strip at the bottom. The open version
 * shows the document on screen; a click opens another one; "New" keeps the cover on screen as a
 * new version; the cover that the song has now is in the strip too.
 */
const VersionsStripComponent: React.FC<VersionsStripProps> = ({ onOpen, onNew, onDelete, isBusy }) => {
  const intl = useIntl();
  const { data, designUuid, state, readOnly } = useCoverEditor();
  // The API lists the version with the newest change first; the strip shows the newest made
  // first, and the numbers count from the first one made.
  const versions = useMemo(
    () =>
      designsByAge(data.designs).flatMap(({ design, number }) => {
        const doc = design.uuid === designUuid ? state.doc : parseCoverDoc(design.doc);
        // A version that does not parse (a later shape) has no picture to show.
        return doc === null ? [] : [{ design, number, doc }];
      }),
    [data.designs, designUuid, state.doc]
  );

  return (
    <footer className={styles.versions}>
      <div className={styles.versionsLabel}>
        <b>{intl.formatMessage(coverEditorMessages.versions)}</b>
        <small>{intl.formatMessage(coverEditorMessages.versionsCount, { count: data.designs.length })}</small>
      </div>
      <div className={styles.versionsList}>
        {versions.map(({ design, number, doc }) => {
          const isOpen = design.uuid === designUuid;
          return (
            <span key={design.uuid} className={clsx(styles.version, isOpen && styles.versionOn)}>
              <button
                type="button"
                className={styles.versionThumb}
                aria-label={intl.formatMessage(coverEditorMessages.versionOpen, { number })}
                aria-pressed={isOpen}
                disabled={isBusy}
                onClick={() => onOpen(design.uuid)}
              >
                <CoverThumb doc={doc} size={THUMB_PX} />
              </button>
              <small>
                {intl.formatMessage(
                  design.appliedAt === null ? coverEditorMessages.versionNumber : coverEditorMessages.versionUsed,
                  { number }
                )}
              </small>
              {!isOpen && !readOnly && versions.length > 1 && (
                <button
                  type="button"
                  className={styles.versionDelete}
                  aria-label={intl.formatMessage(coverEditorMessages.versionDelete, { number })}
                  disabled={isBusy}
                  onClick={() => onDelete(design.uuid)}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        {data.song.artworkUrl !== null && (
          <>
            <span className={styles.topSeparator} />
            <span className={clsx(styles.version, styles.versionCurrent)}>
              <img className={styles.versionCover} src={data.song.artworkUrl} alt="" />
              <small>{intl.formatMessage(coverEditorMessages.versionCoverNow)}</small>
            </span>
          </>
        )}
        <span className={styles.version}>
          <button
            type="button"
            className={styles.versionNew}
            aria-label={intl.formatMessage(coverEditorMessages.versionNew)}
            disabled={isBusy || readOnly}
            onClick={onNew}
          >
            +
          </button>
          <small>{intl.formatMessage(coverEditorMessages.versionNewShort)}</small>
        </span>
      </div>
      <span className={styles.versionsHint}>{intl.formatMessage(coverEditorMessages.versionsHint)}</span>
    </footer>
  );
};

export const VersionsStrip = TypedMemo(VersionsStripComponent);
